import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Lock, CheckCircle2 } from "lucide-react";

/**
 * AuthGateModal — inline login/signup for the deferred-login PropertyForm flow.
 *
 * Why exists: the previous flow forced landlords to create an account BEFORE
 * seeing the listing form. That's a 60–70% drop-off point on Google-Ads
 * landlord traffic. We now let unauthenticated users fill the whole form
 * (photos, price, address, everything) and only prompt for auth when they
 * click Publish. Because the form state lives in the parent component's
 * React state, we MUST keep the user on the same page — an inline modal
 * with email/password login *without* navigating away.
 *
 * Google Auth is intentionally omitted here: it redirects to an external
 * domain (auth.emergentagent.com) which wipes the in-memory form state
 * and any un-uploaded File objects. Owners who want Google can log in via
 * the header and then list. We can add "save-to-sessionStorage → Google
 * redirect → restore" later once File → base64 persistence is designed.
 *
 * Props:
 *   open       — controls dialog visibility
 *   onOpenChange(bool) — dialog close handler
 *   onSuccess() — called after a successful login OR register. Parent
 *                 typically fires the pending property submission here.
 */
export default function AuthGateModal({ open, onOpenChange, onSuccess }) {
  const { login, register } = useAuth();
  const [tab, setTab] = useState("register"); // most landlords are new
  const [busy, setBusy] = useState(false);

  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [regData, setRegData] = useState({ name: "", email: "", password: "", phone: "" });

  const doLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(loginData.email, loginData.password);
      toast.success("Welcome back — publishing your listing…");
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally { setBusy(false); }
  };

  const doRegister = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const refCode = (typeof window !== "undefined" && window.localStorage.getItem("tlr_ref")) || null;
      const res = await register({
        ...regData,
        referral_code: refCode ? refCode.toUpperCase() : null,
      });
      if (refCode) window.localStorage.removeItem("tlr_ref");
      if (res?.bonus_credits) {
        toast.success(`Account created — you got ${res.bonus_credits} free contact reveals 🎁`);
      } else {
        toast.success("Account created — publishing your listing…");
      }
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-2xl" data-testid="auth-gate-modal">
        <DialogHeader>
          <div className="w-12 h-12 rounded-full bg-[#7A1E2A]/10 flex items-center justify-center mb-2">
            <Lock size={20} className="text-[#7A1E2A]" />
          </div>
          <DialogTitle className="font-display text-2xl text-[#1B0F14]">
            Almost done — one last step
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-600">
            Your listing details are ready. Create a quick account (or log in) to publish it.
            Your photos and form data are safe here.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="mt-4">
          <TabsList className="grid grid-cols-2 mb-4">
            <TabsTrigger value="register" data-testid="auth-gate-tab-register">Create account</TabsTrigger>
            <TabsTrigger value="login" data-testid="auth-gate-tab-login">Log in</TabsTrigger>
          </TabsList>

          {/* ─────────────── REGISTER TAB ─────────────── */}
          <TabsContent value="register">
            <form onSubmit={doRegister} className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-gray-700 mb-1 block">Full name</label>
                <Input
                  data-testid="auth-gate-signup-name"
                  value={regData.name}
                  onChange={(e) => setRegData({ ...regData, name: e.target.value })}
                  required
                  className="h-11"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-700 mb-1 block">Email</label>
                <Input
                  data-testid="auth-gate-signup-email"
                  type="email"
                  value={regData.email}
                  onChange={(e) => setRegData({ ...regData, email: e.target.value })}
                  required
                  className="h-11"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-700 mb-1 block">
                  Phone <span className="text-gray-400 font-normal">(tenants call this)</span>
                </label>
                <Input
                  data-testid="auth-gate-signup-phone"
                  value={regData.phone}
                  onChange={(e) => setRegData({ ...regData, phone: e.target.value })}
                  placeholder="+91 ..."
                  className="h-11"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-700 mb-1 block">
                  Password <span className="text-gray-400 font-normal">(min 6 chars)</span>
                </label>
                <Input
                  data-testid="auth-gate-signup-password"
                  type="password"
                  value={regData.password}
                  onChange={(e) => setRegData({ ...regData, password: e.target.value })}
                  minLength={6}
                  required
                  className="h-11"
                />
              </div>
              <Button
                data-testid="auth-gate-signup-submit"
                type="submit"
                disabled={busy}
                className="w-full h-12 rounded-full bg-[#7A1E2A] hover:bg-[#5A1620] text-white font-semibold"
              >
                {busy ? "Creating…" : "Create account & Publish"}
              </Button>
              <div className="flex items-center gap-2 text-xs text-gray-500 pt-1">
                <CheckCircle2 size={12} className="text-green-600" />
                <span>Free forever. No credit card.</span>
              </div>
            </form>
          </TabsContent>

          {/* ─────────────── LOGIN TAB ─────────────── */}
          <TabsContent value="login">
            <form onSubmit={doLogin} className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-gray-700 mb-1 block">Email</label>
                <Input
                  data-testid="auth-gate-login-email"
                  type="email"
                  value={loginData.email}
                  onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                  required
                  className="h-11"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-700 mb-1 block">Password</label>
                <Input
                  data-testid="auth-gate-login-password"
                  type="password"
                  value={loginData.password}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                  required
                  className="h-11"
                />
              </div>
              <Button
                data-testid="auth-gate-login-submit"
                type="submit"
                disabled={busy}
                className="w-full h-12 rounded-full bg-[#7A1E2A] hover:bg-[#5A1620] text-white font-semibold"
              >
                {busy ? "Signing in…" : "Log in & Publish"}
              </Button>
            </form>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
